import Minze, { MinzeElement, Reactive, EventListeners } from 'minze'
import { MODALS, SCREEN_WIDTHS } from '../../constants'
import { Locale } from '../../modules/locale'
import { delay, escapeHtml, unescapeHtml, getVisibleWidth } from '../../modules/utils'
import { Callback } from '../../modules/callback'
import MainModule from '../../modules/main'

const GENDER_SELECT_VALUES = ['notSpecified', 'male', 'female', 'other'].join(',')
const DEFAULT_GENDER_VALUE = 'notSpecified'
const COMMUNICATION_LANGUAGE_SELECT_VALUES = ['fr', 'en'].join(',')
const DEFAULT_COMMUNICATION_LANGUAGE_VALUE = 'fr'

Locale.getInstance().registerKeySet('fr', 'profilePersonal', {
  firstName: 'Prénom',
  lastName: 'Nom',
  gender: 'Genre',
  writeHere: 'Écrivez ici...',
  dateOfBirth: 'Date de naissance',
  dateOfBirthPlaceholder: 'MM/JJ/AAAA',
  dateOfBirthFormat: 'MM/DD/YYYY',
  zipcode: 'Code postal',
  location: 'Lieu de résidence',
  communicationLanguage: 'Langue de communication',
  languageSelectLabels: 'Français,Anglais',
  save: 'Sauvegarder',
  genderSelectLabels: 'Non précisé,Homme,Femme,Autre',
  errors: {
    zipcodeLength: 'Le code postal doit contenir 3 ou 6 caractères',
    shortZipcodeLength: 'Le code postal doit contenir 3 caractères',
    firstName: 'Le prénom est obligatoire',
    lastName: 'Le nom est obligatoire',
  },
  dataSavedNotification: 'Modifications sauvegardés',
})

Locale.getInstance().registerKeySet('en', 'profilePersonal', {
  firstName: 'First name',
  lastName: 'Last name',
  writeHere: 'Write here...',
  gender: 'Gender',
  dateOfBirth: 'Date of birth',
  dateOfBirthPlaceholder: 'MM/DD/YYYY',
  dateOfBirthFormat: 'MM/DD/YYYY',
  zipcode: 'Postal code',
  location: 'Place of residence',
  communicationLanguage: 'Language of communication',
  languageSelectLabels: 'French,English',
  save: 'Save',
  genderSelectLabels: 'Not specified,Male,Female,Other',
  errors: {
    zipcodeLength: 'The zipcode must contain 3 or 6 characters',
    shortZipcodeLength: 'The zipcode must contain 3 characters',
    firstName: 'First name is mandatory',
    lastName: 'Last name is mandatory',
  },
  dataSavedNotification: 'Changes saved',
})

export interface LvcProfilePersonalModalContent {
  isDesktop: boolean
  firstName: string
  lastName: string
  gender: string
  dob: string
  zipcode: string
  location: string
  region: string
}

const ZIPCODE_MASK = 'A#A #A#'

export class LvcProfilePersonalModalContent extends MinzeElement {
  reactive: Reactive = [
    ['isDesktop', true],
    ['firstName', ''],
    ['lastName', ''],
    ['gender', ''],
    ['dob', ''],
    ['zipcode', ''],
    ['location', ''],
    ['region', ''],
  ]

  errors: any = {
    zipcode: false,
    firstName: false,
    lastName: false,
  }

  private _customLocation: string = ''
  private onChangeCb: string = ''
  private onZipcodeChangeCb: string = ''

  onChange() {
    // Get values of all the inputs
    const inputIds = ['firstName', 'lastName', 'gender', 'dob', 'zipcode', 'location']
    const values = inputIds.reduce(
      (acc, id) => {
        const element = this.select(`[name="${id}"]`) as HTMLInputElement
        acc[id] = element?.value || ''
        return acc
      },
      {} as { [key: string]: string }
    )

    // Check if there are any non-empty values
    const hasValues = Object.values(values).some(value => value.trim() !== '')

    // Set the page to dirty or clearDirty accordingly
    const mainModule = MainModule.getInstance()
    const routing = mainModule.getRouting()

    const isDirty = hasValues && inputIds.some(key => this[key] !== values[key])

    if (isDirty) {
      routing.setDirty()
    } else {
      routing.clearDirty()
    }
  }

  async onReady(): Promise<void> {
    this.isDesktop = getVisibleWidth() > SCREEN_WIDTHS.DESKTOP_SMALL

    const mainModule = MainModule.getInstance()

    const userData = mainModule.getAuth().getUserData()

    if (userData && userData.profile) {
      const personalInfo = userData.profile.personalData

      this.firstName = personalInfo.firstName
      this.lastName = personalInfo.lastName
      this.language = personalInfo.lang ?? 'fr'
      this.dob = personalInfo.dob || ''
      this.gender = personalInfo.gender
      this.zipcode = personalInfo.zipcode
      this.location = personalInfo.location
      this.region = personalInfo.region
    }

    this.onChangeCb = Callback.attach(this.onChange.bind(this))
    this.onZipcodeChangeCb = Callback.attach(this.onZipcodeChange.bind(this))

    this.rerender()
  }

  handleAuthChanged = (event: Event) => {
    const detail = (event as CustomEvent<any>).detail

    const userData = detail.user
    if (userData) {
      const mainModule = MainModule.getInstance()

      let ud = mainModule.getAuth().getUserData()
      const personalInfo = ud.profile.personalData

      this.firstName = personalInfo.firstName
      this.lastName = personalInfo.lastName
      this.language = personalInfo.lang ?? DEFAULT_COMMUNICATION_LANGUAGE_VALUE
      this.dob = personalInfo.dob || ''
      this.zipcode = personalInfo.zipcode
      this.location = personalInfo.location
      this.region = personalInfo.region
      this.gender = personalInfo.gender ?? DEFAULT_GENDER_VALUE
    }

    this.rerender()

    this.setLocation(this.zipcode as string)
  }

  onFieldChange = (event: Event) => {}

  onDateChange = async (event: Event) => {
    this.dob = (event as any).detail.value
    await delay(0)
    this.rerender()
  }

  save = async () => {
    for (const key in this.errors) {
      this.errors[key] = false
    }

    const mainModule = MainModule.getInstance()

    const firstNameElement: any = this.select('#firstName')
    const firstName = unescapeHtml(firstNameElement?.value || '')

    const lastNameElement: any = this.select('#lastName')
    const lastName = unescapeHtml(lastNameElement?.value || '')

    const langElement: any = this.select('#language')
    const language = langElement?.value

    const dobElement: any = this.select('[name="dob"]')
    const dob = dobElement?.value

    const zipcodeElement: any = this.select('[name="zipcode"]')
    const zipcode = unescapeHtml(zipcodeElement?.value || '')

    const genderElement: any = this.select('[name="gender"]')
    const gender = genderElement?.value

    this.firstName = firstName
    this.lastName = lastName
    this.language = language
    this.dob = dob
    this.zipcode = zipcode.toUpperCase().trim()
    this.gender = gender

    this.errors.zipcode = false
    let zip = this.zipcode as string

    if (zip && zip.length > 0 && zip.length !== 3 && zip.length !== 7) {
      this.errors.zipcode = Locale.getKey('profilePersonal.errors.zipcodeLength')
      this.rerender()
    } else if (!zip || zip.length === 0) {
      this.errors.zipcode = Locale.getKey('profilePersonal.errors.zipcodeLength')
      this.rerender()
    }

    if (!firstName || firstName.length === 0) {
      this.errors.firstName = Locale.getKey('profilePersonal.errors.firstName')
    }

    if (!lastName || lastName.length === 0) {
      this.errors.lastName = Locale.getKey('profilePersonal.errors.lastName')
    }

    let hasErrors = false
    for (let key in this.errors) {
      if (this.errors[key]) {
        hasErrors = true
        break
      }
    }

    if (hasErrors) {
      this.rerender()
      return
    }

    const profileObject: any = {
      firstName: this.firstName,
      lastName: this.lastName,
      lang: this.language ?? 'fr',
      dob: this.dob,
      zipcode: this.zipcode,
      location: this.location,
      region: this.region,
      gender: this.gender,
    }

    for (let key in profileObject) {
      if (profileObject[key] === undefined) {
        delete profileObject[key]
      }
    }
    await mainModule.getAuth().saveProfile('profile-personal', profileObject)

    this.rerender()
    mainModule.showToastAlert(Locale.getKey('profilePersonal.dataSavedNotification'))
    mainModule.getRouting().clearDirty()
  }

  async onZipcodeChange(value: string): Promise<void> {
    this.onChange()
    await this.setLocation(value?.trim()?.length === 3 || value?.trim()?.length === 7 ? value : '')
  }

  async setLocation(zipcode?: string) {
    let location
    if (zipcode) {
      const geo = MainModule.getInstance().getGeo()
      location = geo ? await geo.getNearestLocationByPostalCode(zipcode) : null
    }

    if (location) {
      this.location = location.name
      this.region = location.region
      this.rerender()
    } else {
      this.location = ''
      this.region = ''
      this.rerender()
    }
  }

  html = () => {
    return `
			<div class="form">
			${
        this.isDesktop
          ? `
						<lvc-box mt="32px" display="flex" columnGap="16px">
							<lvc-box flexGrow="1">
								<lvc-input id="firstName" name="firstName" error="${this.errors.firstName}" placeholder="${Locale.getKey('profilePersonal.writeHere')}" default-value="${escapeHtml(this.firstName as string)}" label="${Locale.getKey('profilePersonal.firstName')}" on-change="${this.onChangeCb}"></lvc-input>
							</lvc-box>
							<lvc-box flexGrow="1">
							<lvc-input id="lastName" name="lastName" error="${this.errors.lastName}" placeholder="${Locale.getKey('profilePersonal.writeHere')}" default-value="${escapeHtml(this.lastName as string)}" label="${Locale.getKey('profilePersonal.lastName')}" on-change="${this.onChangeCb}"></lvc-input>
							</lvc-box>
						</lvc-box>

					`
          : `<lvc-box mt="32px">
						 <lvc-input id="firstName" name="firstName" error="${this.errors.firstName}" placeholder="${Locale.getKey('profilePersonal.writeHere')}" default-value="${escapeHtml(this.firstName as string)}" label="${Locale.getKey('profilePersonal.firstName')}" on-change="${this.onChangeCb}"></lvc-input>
					 </lvc-box>
					 <lvc-box mt="16px">
						 <lvc-input id="lastName" name="lastName" error="${this.errors.lastName}" placeholder="${Locale.getKey('profilePersonal.writeHere')}" default-value="${escapeHtml(this.lastName as string)}" label="${Locale.getKey('profilePersonal.lastName')}" on-change="${this.onChangeCb}"></lvc-input>
					 </lvc-box>
					 `
      }
				<lvc-box mt="16px">
					<lvc-select id="gender" name="gender" placeholder="" default-value="${this.gender}" label="${Locale.getKey('profilePersonal.gender')}" selectOptions="${GENDER_SELECT_VALUES}" selectOptionLabels="${Locale.getKey('profilePersonal.genderSelectLabels')}" onChange="${this.onChangeCb}">
					</lvc-select>
				</lvc-box>
				<lvc-box mt="16px">
					<lvc-datepicker id="dob" name="dob" default-value="${this.dob}" value="${this.dob}" label="${Locale.getKey('profilePersonal.dateOfBirth')}" onChange="${this.onChangeCb}"></lvc-datepicker>
				</lvc-box>
				<lvc-box mt="16px" display="flex" columnGap="16px">

					<lvc-input id="address" mask="${ZIPCODE_MASK}" error="${this.errors.zipcode}" name="zipcode" placeholder="ex: A1A" default-value="${escapeHtml((this.zipcode as string) || '')}" label="${Locale.getKey('profilePersonal.zipcode')}" on-change="${this.onZipcodeChangeCb}"></lvc-input>
					<lvc-input id="location" name="location" readonly="true" default-value="${escapeHtml((this.location as string) || '')}" label="${Locale.getKey('profilePersonal.location')}"></lvc-input>
				</lvc-box>
				<lvc-box mt="16px">
					<lvc-select id="language" name="language" default-value="${this.language}" label="${Locale.getKey('profilePersonal.communicationLanguage')}" selectOptions="${COMMUNICATION_LANGUAGE_SELECT_VALUES}" selectOptionLabels="${Locale.getKey('profilePersonal.languageSelectLabels')}" onChange="${this.onChangeCb}">

					</lvc-select>
				</lvc-box>
				<lvc-box width="100%" mt="32px" class="button-wrapper">
					<lvc-button enable-hover-box-shadow="true" width="100%" id="btn-save">
						${Locale.getKey('profilePersonal.save')}
					</lvc-button>
				<lvc-box>
			</div>
		`
  }

  css = () => `
    :host {

    }
    @media screen and (max-width: 991px) {
      .button-wrapper {
        position: fixed;
        bottom: 73px;
        left: 0;
        right: 0;
        padding: 18px 16px;
        width: 100%;
        height: fit-content;
        max-width: calc(540px + 16*2px);
        margin: auto;
      }
    }
  `

  eventListeners: EventListeners = [
    [window, 'lvc:user_state_changed', this.handleAuthChanged.bind(this)],
    ['#btn-save', 'click', this.save.bind(this)],
    ['[name="dob"]', 'change', this.onDateChange.bind(this)],
    //["[name]", "change", this.onFieldChange],
  ]
}
